if (!customElements.get('geo-image-banner')) {
	class GeoImageBanner extends HTMLElement {
		static API_URL = 'https://utils.animalfarm.inc/geolocation/ip';
		static CACHE_KEY = 'geo-location-cache';
		static MS_PER_HOUR = 3600000;
		static KM_TO_MILES = 0.621371;
		static EARTH_RADIUS_MILES = 3958.8;
		static DEFAULT_RADIUS_MILES = 30;
		static DEFAULT_CACHE_TTL_HOURS = 24;
		static DEFAULT_TARGET_COUNTRY = 'US';

		// In Shopify's theme editor, the Liquid template omits `hidden`,
		// so this guard skips geo evaluation and keeps the banner visible.
		async connectedCallback() {
			if (!this.hidden) {
				return;
			}

			const config = this.readConfiguration();
			const geoData = await this.resolveGeoData(config.cacheTtlHours);
			const gates = GeoImageBanner.buildGates(config);

			if (GeoImageBanner.shouldReveal(gates, geoData)) {
				this.removeAttribute('hidden');
			}
		}

		readConfiguration() {
			const targetState = (this.dataset.targetState || '').trim();
			const targetLat = parseFloat(this.dataset.targetLat);
			const targetLng = parseFloat(this.dataset.targetLng);
			const hasCoordinates = !isNaN(targetLat) && !isNaN(targetLng);

			return {
				targetCoords: hasCoordinates ? { lat: targetLat, lng: targetLng } : null,
				targetState,
				targetCountry: this.dataset.targetCountry || GeoImageBanner.DEFAULT_TARGET_COUNTRY,
				radiusMiles: parseFloat(this.dataset.radiusMiles) || GeoImageBanner.DEFAULT_RADIUS_MILES,
				cacheTtlHours: parseInt(this.dataset.cacheTtl, 10) || GeoImageBanner.DEFAULT_CACHE_TTL_HOURS,
				customerState: this.dataset.customerState || '',
			};
		}

		async resolveGeoData(cacheTtlHours) {
			const cached = this.readCache(cacheTtlHours);
			if (cached) {
				return cached;
			}

			const fresh = await GeoImageBanner.fetchGeoData();
			if (fresh) {
				this.writeCache(fresh);
			}

			return fresh;
		}

		readCache(ttlHours) {
			try {
				const raw = localStorage.getItem(GeoImageBanner.CACHE_KEY);
				if (!raw) {
					return null;
				}

				const cached = JSON.parse(raw);
				const ageMs = Date.now() - cached.timestamp;
				const isExpired = ageMs > ttlHours * GeoImageBanner.MS_PER_HOUR;

				return isExpired ? null : cached.data;
			} catch {
				return null;
			}
		}

		static async fetchGeoData() {
			try {
				const response = await fetch(GeoImageBanner.API_URL);
				if (!response.ok) {
					throw new Error('Geo API responded with HTTP ' + response.status);
				}
				return await response.json();
			} catch (error) {
				console.warn('[geo-image-banner] API request failed:', error);
				return null;
			}
		}

		writeCache(data) {
			try {
				const entry = JSON.stringify({ data, timestamp: Date.now() });
				localStorage.setItem(GeoImageBanner.CACHE_KEY, entry);
			} catch {
				// localStorage may be unavailable (private browsing, quota exceeded)
			}
		}

		static shouldReveal(gates, geoData) {
			return gates.every((gate) => gate(geoData));
		}

		// Gates are only included when their config field is set, so an empty
		// array (no targeting configured) causes "every()" to return true.
		static buildGates(config) {
			const gates = [];

			if (config.targetCountry) {
				gates.push((geoData) => GeoImageBanner.visitorInCountry(geoData, config));
			}

			if (config.targetState) {
				gates.push((geoData) => GeoImageBanner.visitorInState(geoData, config));
			}

			if (config.targetCoords) {
				gates.push((geoData) => GeoImageBanner.visitorInRadius(geoData, config));
			}

			return gates;
		}

		// Graceful on API failure: missing geo data passes rather than hiding.
		static visitorInCountry(geoData, config) {
			if (!geoData?.country) {
				return true;
			}

			return geoData.country.toUpperCase() === config.targetCountry.toUpperCase();
		}

		// Falls back to the customer's default-address state when the geo API has no subdivision.
		static visitorInState(geoData, config) {
			const ipState = geoData?.largestSubdivision;
			if (GeoImageBanner.statesEqual(ipState, config.targetState)) {
				return true;
			}

			// Intentional: customerState is populated server-side from the logged-in
			// customer's default Shopify address province — a logged-in customer whose
			// default address is in Idaho should still see an Idaho-only banner even
			// when traveling out of state and browsing from a non-Idaho IP.
			return GeoImageBanner.statesEqual(config.customerState, config.targetState);
		}

		// Case-insensitive: state names from the geo API, Shopify schema, and customer addresses may differ in casing.
		static statesEqual(stateA, stateB) {
			if (!stateA || !stateB) {
				return false;
			}

			return stateA.trim().toUpperCase() === stateB.trim().toUpperCase();
		}

		// Strict (unlike the country gate): missing coords or imprecise IP hides the banner.
		static visitorInRadius(geoData, config) {
			const visitorCoords = GeoImageBanner.extractCoordinates(geoData);
			if (!visitorCoords) {
				return false;
			}

			const accuracyMiles = (geoData.accuracyRadius || 0) * GeoImageBanner.KM_TO_MILES;
			if (accuracyMiles > config.radiusMiles) {
				return false;
			}

			const distance = GeoImageBanner.haversineDistance(visitorCoords, config.targetCoords);
			return distance <= config.radiusMiles;
		}

		static extractCoordinates(geoData) {
			if (!geoData || geoData.latitude == null || geoData.longitude == null) {
				return null;
			}

			return { lat: geoData.latitude, lng: geoData.longitude };
		}

		/**
		 * Haversine formula: great-circle distance over Earth's surface.
		 *
		 *   a = sin²(Δlat / 2) + cos(lat1) · cos(lat2) · sin²(Δlng / 2)
		 *   d = 2r · arcsin(√a)
		 *
		 * Reference: https://en.wikipedia.org/wiki/Haversine_formula
		 */
		static haversineDistance(point1, point2) {
			const toRadians = (degrees) => (degrees * Math.PI) / 180;

			const deltaLat = toRadians(point2.lat - point1.lat);
			const deltaLng = toRadians(point2.lng - point1.lng);
			const lat1Rad = toRadians(point1.lat);
			const lat2Rad = toRadians(point2.lat);

			const haversineOfCentralAngle =
				Math.pow(Math.sin(deltaLat / 2), 2) +
				Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.pow(Math.sin(deltaLng / 2), 2);

			const centralAngle = 2 * Math.asin(Math.sqrt(haversineOfCentralAngle));

			return GeoImageBanner.EARTH_RADIUS_MILES * centralAngle;
		}
	}

	customElements.define('geo-image-banner', GeoImageBanner);
}
